import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AdvertisementType } from 'constant/advertisement'
import { formatDateTime } from 'utils/timeFormat'
import type {
  SchemaOholoMaterialShow,
  SchemaMemberAnnounceShow,
  // SchemaCmsPublishResults,
  // SchemaVideoRegion,
  // SchemaTopicShow,
  // SchemaDomainShow,
} from 'api'

interface FullPageErrorInfo {
  title: string
  message: string
  hideButton?: boolean
}

interface MaintenanceMessageInfo {
  end: string
  message: string
}

interface FullOrigins {
  domain_original: string[]
  name_original: string[]
  domain_preview: string[]
  domain_picture: string[]
  domain_comic: string[]
  domain_novel: string[]
}

interface OfficialOriginItem {
  name: string
  domain: string
}

interface Station {
  chinese_name: string
  cover_key: string
  enable_share?: boolean
  /** 發現頁「UP主」TAB 開關(1:顯示 2:隱藏)，來自 /system/station */
  show_uploader_tab?: number
  id: number
  is_free: boolean
  is_video_manager: boolean
  is_video_pick_mode: boolean
  mode: string
  name: string
}

export type AdInfoMap = {
  [key: number]: SchemaOholoMaterialShow[]
}

export const MOBILE_CONFIG_UPDATE_REASON_CONFIG = 'config'
export const MOBILE_CONFIG_UPDATE_REASON_ENTRY_DOMAIN = 'entry-domain'

export type MobileConfigUpdateReason =
  | typeof MOBILE_CONFIG_UPDATE_REASON_CONFIG
  | typeof MOBILE_CONFIG_UPDATE_REASON_ENTRY_DOMAIN

interface AppState {
  name: string
  hero: object
  currentSplashAd: { item: SchemaOholoMaterialShow; img: string } | null
  isReadyToShowSplashAd: boolean
  isReadyToEnter: boolean
  isViewedAdvertisement: boolean
  isShowingLaunchSplash: boolean
  isShowingMaintenancePage: boolean
  isShowingMobileConfigUpdate: boolean
  isViewedMobileConfigUpdate: boolean
  mobileConfigUpdateReason: MobileConfigUpdateReason | null
  maintenanceMessageInfo: MaintenanceMessageInfo
  isShowingFullPageError: boolean
  fullPageError: FullPageErrorInfo | null
  fullOrigins: FullOrigins
  officialOrigin: string
  officialOriginList: OfficialOriginItem[]
  previewOrigin: string
  pictureOrigin: string
  comicOrigin: string
  novelOrigin: string
  navigationContent: unknown[]
  tagList: unknown[]
  actorList: unknown[]
  producerList: unknown[]
  regionList: unknown[]
  topicList: unknown[]
  announcementList: unknown[]
  isSkippedAnnouncement: boolean
  isNoticePopupDismissed: boolean
  isIDCardPopupDismissed: boolean
  memberAnnounces: SchemaMemberAnnounceShow[] | null
  adInfo: AdInfoMap
  station: Station
  routeDetectMessage: string
  tempVideoPageHost: string
  enableHtmlUpdatedAlert: boolean
}

const initialState: AppState = {
  name: 'Nova',
  hero: {},
  /**
   * 開屏廣告內容
   */
  currentSplashAd: null,
  /** 顯示開屏廣告需要的資料已讀取完成 */
  isReadyToShowSplashAd: false,
  /** App 必需的資料是否已讀取完成 */
  isReadyToEnter: false,
  /** 是否看過廣告 */
  isViewedAdvertisement: false,
  /** 是否正在顯示啟動頁 */
  isShowingLaunchSplash: true,
  /** 是否顯示維護頁 */
  isShowingMaintenancePage: false,
  /** 是否顯示描述檔強制更新 */
  isShowingMobileConfigUpdate: false,
  /** 是否看過描述檔強制更新 */
  isViewedMobileConfigUpdate: false,
  /** 描述檔強制更新原因 */
  mobileConfigUpdateReason: null,
  /** 維護訊息 */
  maintenanceMessageInfo: {
    end: '',
    message: '',
  },
  isShowingFullPageError: false,
  /** 完整頁面錯誤資訊 */
  fullPageError: null,
  /** 完整路徑資料，除錯目的 */
  fullOrigins: {
    domain_original: [],
    name_original: [],
    domain_preview: [],
    domain_picture: [],
    domain_comic: [],
    domain_novel: [],
  },
  /** 影片正式路徑 */
  officialOrigin: '',
  /** full origin list */
  officialOriginList: [
    /*
    {
      name: 'domainName',
      domain: 'do.main'
    }
    */
  ],
  /** 影片預覽路徑 */
  previewOrigin: '',
  /** base64 圖片路徑 */
  pictureOrigin: '',
  /** */
  comicOrigin: '',
  /** */
  novelOrigin: '',
  /** CMS Nav資訊 */
  navigationContent: [],
  /** 標籤列表 */
  tagList: [],
  /** 片商列表 */
  actorList: [],
  producerList: [],
  /** 地區列表 */
  regionList: [],
  /** 視頻/短視頻主題 */
  topicList: [],
  /** 公告列表 */
  announcementList: [],
  /** 跳過公告 */
  isSkippedAnnouncement: false,
  /** 圖片公告彈窗已關閉 */
  isNoticePopupDismissed: false,
  /** 身分卡彈窗已關閉 */
  isIDCardPopupDismissed: false,
  /** 會員彈窗原始資料（null = 尚未載入，由頁面層判斷開關） */
  memberAnnounces: null as SchemaMemberAnnounceShow[] | null,
  /** 廣告資訊 */
  adInfo: {
    [AdvertisementType.Entrance]: [],
    [AdvertisementType.NavBanner]: [],
    [AdvertisementType.AreaBottom]: [],
    [AdvertisementType.ViewMore]: [],
    [AdvertisementType.AreaCentral]: [],
    [AdvertisementType.ViewVideoBanner]: [],
    [AdvertisementType.Profile]: [],
    [AdvertisementType.Announcement]: [],
    [AdvertisementType.AppCenterBanner]: [],
    [AdvertisementType.AppCenterIcon]: [],
    [AdvertisementType.Category]: [],
  },
  /** 站台資訊 */
  station: {
    chinese_name: '',
    cover_key: '',
    enable_share: false,
    show_uploader_tab: 2,
    id: 0,
    is_free: false,
    is_video_manager: false,
    is_video_pick_mode: false,
    mode: '',
    name: '',
  },
  routeDetectMessage: '加载中',
  tempVideoPageHost: '',
  enableHtmlUpdatedAlert: true,
}

const slice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    initApp: (state, action: PayloadAction<void>) => {
      state = {
        ...initialState,
      }
    },
    updateCurrentSplashAd: (state, action: PayloadAction<AppState['currentSplashAd']>) => {
      state.currentSplashAd = action.payload
    },
    updateReadyToShowSplashAd: (state, action: PayloadAction<boolean>) => {
      state.isReadyToShowSplashAd = action.payload
    },
    updateReadyToEnter: (state, action: PayloadAction<boolean>) => {
      state.isReadyToEnter = action.payload
    },
    updateAppName: (state, action: PayloadAction<string>) => {
      state.name = action.payload
    },
    updateHero: (state, action: PayloadAction<object>) => {
      state.hero = action.payload
    },
    updateNavigationContent: (state, action: PayloadAction<unknown[]>) => {
      state.navigationContent = action.payload
    },
    updateTagList: (state, action: PayloadAction<unknown[]>) => {
      state.tagList = action.payload
    },
    updateRegionList: (state, action: PayloadAction<unknown[]>) => {
      state.regionList = action.payload
    },
    updateTopicList: (state, action: PayloadAction<unknown[]>) => {
      state.topicList = action.payload
    },
    updateProducerList: (state, action: PayloadAction<unknown[]>) => {
      state.producerList = action.payload
    },
    updateViewedAdvertisement: (state, action: PayloadAction<boolean>) => {
      state.isViewedAdvertisement = action.payload
    },
    updateFullOrigins: (state, action: PayloadAction<FullOrigins>) => {
      state.fullOrigins = action.payload
    },
    updatePreviewOrigin: (state, action: PayloadAction<string>) => {
      state.previewOrigin = action.payload
    },
    updateOfficialOrigin: (state, action: PayloadAction<string>) => {
      state.officialOrigin = action.payload
    },
    updateOfficialOriginList: (state, action: PayloadAction<OfficialOriginItem[]>) => {
      state.officialOriginList = action.payload
    },
    updatePictureOrigin: (state, action: PayloadAction<string>) => {
      state.pictureOrigin = action.payload
    },
    updateComicOrigin: (state, action: PayloadAction<string>) => {
      state.comicOrigin = action.payload
    },
    updateNovelOrigin: (state, action: PayloadAction<string>) => {
      state.novelOrigin = action.payload
    },
    updateAdInfo: (state, action: PayloadAction<AdInfoMap>) => {
      state.adInfo = action.payload
    },
    updateAnnouncementList: (state, action: PayloadAction<unknown[]>) => {
      state.announcementList = action.payload
    },
    updateIsSkippedAnnouncement: (state, action: PayloadAction<boolean>) => {
      state.isSkippedAnnouncement = action.payload
    },
    dismissNoticePopup: (state) => {
      state.isNoticePopupDismissed = true
    },
    dismissIDCardPopup: (state) => {
      state.isIDCardPopupDismissed = true
    },
    updateMemberAnnounces: (state, action: PayloadAction<SchemaMemberAnnounceShow[]>) => {
      state.memberAnnounces = action.payload
    },
    updateStation: (state, action: PayloadAction<Station>) => {
      state.station = action.payload as typeof state.station
    },
    updateRouteDetectMessage: (state, action: PayloadAction<string>) => {
      state.routeDetectMessage = action.payload
    },
    updateIsShowingLaunchSplash: (state, action: PayloadAction<boolean>) => {
      state.isShowingLaunchSplash = action.payload
    },
    updateMaintenanceSplash: (state, action: PayloadAction<{ visible: boolean; end: string; message: string }>) => {
      state.isShowingMaintenancePage = action.payload.visible
      state.maintenanceMessageInfo.end = formatDateTime(action.payload.end) || ''
      state.maintenanceMessageInfo.message = action.payload.message
    },
    updateFullPageError: (state, action: PayloadAction<{ visible: boolean; error: FullPageErrorInfo | null }>) => {
      state.isShowingFullPageError = action.payload.visible
      state.fullPageError = action.payload.error
    },
    updateShowMobileConfigUpdate: (state, action: PayloadAction<boolean>) => {
      state.isShowingMobileConfigUpdate = action.payload
    },
    updateMobileConfigUpdateReason: (state, action: PayloadAction<MobileConfigUpdateReason | null>) => {
      state.mobileConfigUpdateReason = action.payload
    },
    updateViewedMobileConfigUpdate: (state, action: PayloadAction<boolean>) => {
      state.isViewedMobileConfigUpdate = action.payload
    },
    updateTempVideoPageHost: (state, action: PayloadAction<string>) => {
      state.tempVideoPageHost = action.payload
    },
    updateEnableHtmlUpdatedAlert: (state, action: PayloadAction<boolean>) => {
      state.enableHtmlUpdatedAlert = action.payload
    },
  },
})

export const { actions } = slice
export default slice.reducer
